import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Palette, Type, Layout, Share2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { brandApi } from '@/api/landingPages';
import { ImagePicker } from '@/components/ImagePicker';
import BrandFooter from '@/components/brand/BrandFooter';

function ColorInput({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
        style={{ backgroundColor: value || '#ffffff' }}
        onClick={() => document.getElementById(`color-${label}`)?.click()}
      />
      <div className="flex-1">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <Input
          id={`color-${label}`}
          type="color"
          value={value || '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          className="sr-only"
        />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="h-8 text-sm font-mono"
        />
      </div>
    </div>
  );
}

export default function BrandKit() {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  useEffect(() => {
    fetchBrand();
  }, []);

  const fetchBrand = async () => {
    setLoading(true);
    try {
      const data = await brandApi.getSettings();
      if (data.success) {
        setBrand(data.data);
      }
    } catch (error) {
      console.error('Error fetching brand:', error);
      toast.error('Failed to load brand settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await brandApi.updateSettings({
        logo_url: brand.logo_url,
        logo_dark_url: brand.logo_dark_url,
        favicon_url: brand.favicon_url,
        colors: brand.colors,
        typography: brand.typography,
        footer: brand.footer,
        social_links: brand.social_links,
        custom_css: brand.custom_css
      });
      if (data.success) {
        toast.success('Brand settings saved!');
        setBrand(data.data);
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save brand settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const data = await brandApi.resetToDefaults();
      if (data.success) {
        toast.success('Reset to defaults');
        setBrand(data.data);
        setResetDialogOpen(false);
      }
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const updateColors = (key, value) => {
    setBrand({
      ...brand,
      colors: { ...brand.colors, [key]: value }
    });
  };

  const updateTypography = (key, value) => {
    setBrand({
      ...brand,
      typography: { ...brand.typography, [key]: value }
    });
  };

  const updateFooter = (key, value) => {
    setBrand({
      ...brand,
      footer: { ...brand.footer, [key]: value }
    });
  };

  const updateSocial = (key, value) => {
    setBrand({
      ...brand,
      social_links: { ...brand.social_links, [key]: value }
    });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="brand-kit-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-[400px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="brand-kit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Brand Kit</h1>
          <p className="text-muted-foreground">Manage your brand assets for landing pages</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reset Brand Settings?</DialogTitle>
                <DialogDescription>
                  This will reset all brand settings to their default values. This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setResetDialogOpen(false)}>Cancel</Button>
                <Button variant="destructive" onClick={handleReset}>Reset</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={handleSave} disabled={saving} data-testid="save-brand-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="colors">
        <TabsList className="grid grid-cols-4 w-fit">
          <TabsTrigger value="colors" className="gap-2">
            <Palette className="w-4 h-4" />
            Colors
          </TabsTrigger>
          <TabsTrigger value="typography" className="gap-2">
            <Type className="w-4 h-4" />
            Typography
          </TabsTrigger>
          <TabsTrigger value="footer" className="gap-2">
            <Layout className="w-4 h-4" />
            Footer
          </TabsTrigger>
          <TabsTrigger value="social" className="gap-2">
            <Share2 className="w-4 h-4" />
            Social
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          {/* Colors */}
          <TabsContent value="colors">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Logo & Icons</CardTitle>
                  <CardDescription>Your brand logos and favicon</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ImagePicker
                    label="Logo (Light Background)"
                    value={brand?.logo_url || ''}
                    onChange={(url) => setBrand({ ...brand, logo_url: url })}
                    placeholder="Select or upload logo"
                    aspectRatio="auto"
                    folder="brand"
                    testId="brand-logo-light-picker"
                  />
                  <ImagePicker
                    label="Logo (Dark Background)"
                    value={brand?.logo_dark_url || ''}
                    onChange={(url) => setBrand({ ...brand, logo_dark_url: url })}
                    placeholder="Select or upload dark mode logo"
                    aspectRatio="auto"
                    folder="brand"
                    testId="brand-logo-dark-picker"
                  />
                  <ImagePicker
                    label="Favicon"
                    value={brand?.favicon_url || ''}
                    onChange={(url) => setBrand({ ...brand, favicon_url: url })}
                    placeholder="Select or upload favicon"
                    aspectRatio="1/1"
                    folder="brand"
                    testId="brand-favicon-picker"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Color Palette</CardTitle>
                  <CardDescription>Brand colors for your pages</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <ColorInput
                      label="Primary"
                      value={brand?.colors?.primary}
                      onChange={(v) => updateColors('primary', v)}
                    />
                    <ColorInput
                      label="Secondary"
                      value={brand?.colors?.secondary}
                      onChange={(v) => updateColors('secondary', v)}
                    />
                  </div>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <ColorInput
                      label="Page Background"
                      value={brand?.colors?.background}
                      onChange={(v) => updateColors('background', v)}
                    />
                    <ColorInput
                      label="Text"
                      value={brand?.colors?.text}
                      onChange={(v) => updateColors('text', v)}
                    />
                    <ColorInput
                      label="Muted Text"
                      value={brand?.colors?.text_muted}
                      onChange={(v) => updateColors('text_muted', v)}
                    />
                  </div>
                  <Separator />
                  <p className="text-sm font-medium text-muted-foreground">Section Backgrounds</p>
                  <div className="grid grid-cols-2 gap-4">
                    <ColorInput
                      label="Section Background"
                      value={brand?.colors?.section_background}
                      onChange={(v) => updateColors('section_background', v)}
                    />
                    <ColorInput
                      label="Alternating Section"
                      value={brand?.colors?.section_background_alt}
                      onChange={(v) => updateColors('section_background_alt', v)}
                    />
                  </div>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <ColorInput
                      label="Button"
                      value={brand?.colors?.button}
                      onChange={(v) => updateColors('button', v)}
                    />
                    <ColorInput
                      label="Button Text"
                      value={brand?.colors?.button_text}
                      onChange={(v) => updateColors('button_text', v)}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Typography */}
          <TabsContent value="typography">
            <Card>
              <CardHeader>
                <CardTitle>Typography Settings</CardTitle>
                <CardDescription>Font families and weights</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Heading Font</Label>
                      <Input
                        value={brand?.typography?.heading_font || ''}
                        onChange={(e) => updateTypography('heading_font', e.target.value)}
                        placeholder="Montserrat"
                      />
                    </div>
                    <div>
                      <Label>Heading Weight</Label>
                      <Input
                        value={brand?.typography?.heading_weight || ''}
                        onChange={(e) => updateTypography('heading_weight', e.target.value)}
                        placeholder="700"
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label>Body Font</Label>
                      <Input
                        value={brand?.typography?.body_font || ''}
                        onChange={(e) => updateTypography('body_font', e.target.value)}
                        placeholder="Inter"
                      />
                    </div>
                    <div>
                      <Label>Body Weight</Label>
                      <Input
                        value={brand?.typography?.body_weight || ''}
                        onChange={(e) => updateTypography('body_weight', e.target.value)}
                        placeholder="400"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-8 p-6 border rounded-lg bg-muted/20">
                  <p className="text-sm text-muted-foreground mb-4">Preview</p>
                  <h1
                    className="text-3xl mb-2"
                    style={{
                      fontFamily: brand?.typography?.heading_font || 'inherit',
                      fontWeight: brand?.typography?.heading_weight || 700
                    }}
                  >
                    Heading Example
                  </h1>
                  <p
                    style={{
                      fontFamily: brand?.typography?.body_font || 'inherit',
                      fontWeight: brand?.typography?.body_weight || 400
                    }}
                  >
                    This is an example of body text using your selected fonts and weights.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Footer */}
          <TabsContent value="footer">
            <Card>
              <CardHeader>
                <CardTitle>Footer Settings</CardTitle>
                <CardDescription>Default footer content for all pages</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={brand?.footer?.company_name || ''}
                      onChange={(e) => updateFooter('company_name', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input
                      value={brand?.footer?.tagline || ''}
                      onChange={(e) => updateFooter('tagline', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Address</Label>
                  <Textarea
                    value={brand?.footer?.address || ''}
                    onChange={(e) => updateFooter('address', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={brand?.footer?.phone || ''}
                      onChange={(e) => updateFooter('phone', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={brand?.footer?.email || ''}
                      onChange={(e) => updateFooter('email', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Website URL</Label>
                  <Input
                    value={brand?.footer?.website_url || ''}
                    onChange={(e) => updateFooter('website_url', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Copyright Text</Label>
                  <Input
                    value={brand?.footer?.copyright_text || ''}
                    onChange={(e) => updateFooter('copyright_text', e.target.value)}
                  />
                </div>
                <Separator />
                <h4 className="font-medium">Footer Appearance</h4>
                <div className="grid grid-cols-2 gap-4">
                  <ColorInput
                    label="Background Color"
                    value={brand?.footer?.background_color || '#1f2937'}
                    onChange={(v) => updateFooter('background_color', v)}
                  />
                  <ColorInput
                    label="Text Color"
                    value={brand?.footer?.text_color || '#ffffff'}
                    onChange={(v) => updateFooter('text_color', v)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <ColorInput
                    label="Link Color"
                    value={brand?.footer?.link_color || '#60a5fa'}
                    onChange={(v) => updateFooter('link_color', v)}
                  />
                  <ColorInput
                    label="Link Hover Color"
                    value={brand?.footer?.link_hover_color || '#93c5fd'}
                    onChange={(v) => updateFooter('link_hover_color', v)}
                  />
                </div>
                {/* Footer Preview — renders the SAME component the public page
                    uses, so what you see here is exactly what visitors get.
                    It used to be a separate hand-rolled block showing a
                    "Sample Link" that appeared on no real page. */}
                <div className="mt-4 overflow-hidden rounded-lg border border-border">
                  <p className="bg-muted px-3 py-1.5 text-xs text-muted-foreground">
                    Live preview — this is the footer visitors see
                  </p>
                  <BrandFooter brand={brand} />
                </div>
                <Separator />
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={brand?.footer?.show_social_links !== false}
                      onCheckedChange={(v) => updateFooter('show_social_links', v)}
                    />
                    <Label>Show Social Links</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={brand?.footer?.show_powered_by !== false}
                      onCheckedChange={(v) => updateFooter('show_powered_by', v)}
                    />
                    <Label>Show Powered By</Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social */}
          <TabsContent value="social">
            <Card>
              <CardHeader>
                <CardTitle>Social Media Links</CardTitle>
                <CardDescription>Links shown in page footers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Facebook</Label>
                    <Input
                      value={brand?.social_links?.facebook || ''}
                      onChange={(e) => updateSocial('facebook', e.target.value)}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div>
                    <Label>Instagram</Label>
                    <Input
                      value={brand?.social_links?.instagram || ''}
                      onChange={(e) => updateSocial('instagram', e.target.value)}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div>
                    <Label>Twitter / X</Label>
                    <Input
                      value={brand?.social_links?.twitter || ''}
                      onChange={(e) => updateSocial('twitter', e.target.value)}
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                  <div>
                    <Label>LinkedIn</Label>
                    <Input
                      value={brand?.social_links?.linkedin || ''}
                      onChange={(e) => updateSocial('linkedin', e.target.value)}
                      placeholder="https://linkedin.com/..."
                    />
                  </div>
                  <div>
                    <Label>YouTube</Label>
                    <Input
                      value={brand?.social_links?.youtube || ''}
                      onChange={(e) => updateSocial('youtube', e.target.value)}
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                  <div>
                    <Label>TikTok</Label>
                    <Input
                      value={brand?.social_links?.tiktok || ''}
                      onChange={(e) => updateSocial('tiktok', e.target.value)}
                      placeholder="https://tiktok.com/..."
                    />
                  </div>
                  <div>
                    <Label>WhatsApp</Label>
                    <Input
                      value={brand?.social_links?.whatsapp || ''}
                      onChange={(e) => updateSocial('whatsapp', e.target.value)}
                      placeholder="https://wa.me/..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
